import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { PROFILE_LABELS, useAccess } from '../access.js';

export default function Administration() {
  const { user, can } = useAccess();
  const qc = useQueryClient();
  const tenantId = user?.tenant?.id;
  const [tenant, setTenant] = useState({ name: '', slug: '' });
  const [member, setMember] = useState({ email: '', role: 'viewer', status: 'active' });
  const tenantsQ = useQuery({ queryKey: ['tenants'], queryFn: api.listTenants });
  const membersQ = useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => api.tenantMembers(tenantId),
    enabled: !!tenantId && can('tenant.members.manage'),
  });
  const create = useMutation({
    mutationFn: () => api.createTenant(tenant),
    onSuccess: () => {
      setTenant({ name: '', slug: '' });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
  const save = useMutation({
    mutationFn: (input) => api.setTenantMember(tenantId, input),
    onSuccess: () => {
      setMember({ email: '', role: 'viewer', status: 'active' });
      qc.invalidateQueries({ queryKey: ['members', tenantId] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const select = useMutation({
    mutationFn: api.selectTenant,
    onSuccess: () => window.location.assign('/admin'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Administração</h1>
        <p className="text-slate-500 text-sm">Gerencie tenants e os acessos às suas bases.</p>
      </div>
      {can('tenants.manage') && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="font-semibold">Tenants</h2>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <label className="text-sm">
              Nome
              <input
                required
                className="input block"
                value={tenant.name}
                onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Identificador
              <input
                required
                pattern="[a-z][a-z0-9-]{1,59}"
                placeholder="minha-empresa"
                className="input block"
                value={tenant.slug}
                onChange={(e) => setTenant({ ...tenant, slug: e.target.value })}
              />
            </label>
            <button className="btn-primary" disabled={create.isPending}>
              {create.isPending ? 'Criando…' : 'Criar tenant'}
            </button>
          </form>
          {create.error && (
            <p role="alert" className="text-red-600 text-sm">
              {create.error.message}
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {(tenantsQ.data ?? []).map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between">
                <span>
                  {t.name} <span className="text-slate-500 text-sm">{t.slug}</span>
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={select.isPending || t.id === tenantId || t.status !== 'active'}
                  onClick={() => select.mutate(t.id)}
                >
                  {t.id === tenantId ? 'Tenant atual' : 'Administrar'}
                </button>
              </li>
            ))}
          </ul>
          {select.error && (
            <p role="alert" className="text-red-600 text-sm">
              {select.error.message}
            </p>
          )}
        </section>
      )}
      {tenantId && can('tenant.members.manage') ? (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="font-semibold">Usuários de {user.tenant.name}</h2>
          <p className="text-sm text-slate-500">
            Vincule uma Conta Nexify existente pelo e-mail. O perfil vale somente neste tenant.
          </p>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate(member);
            }}
          >
            <label className="text-sm">
              E-mail
              <input
                required
                type="email"
                className="input block"
                value={member.email}
                onChange={(e) => setMember({ ...member, email: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Perfil
              <select
                className="input block"
                value={member.role}
                onChange={(e) => setMember({ ...member, role: e.target.value })}
              >
                {['tenant_admin', 'campaign_user', 'viewer'].map((role) => (
                  <option key={role} value={role}>
                    {PROFILE_LABELS[role]}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar acesso'}
            </button>
          </form>
          {save.error && (
            <p role="alert" className="text-red-600 text-sm">
              {save.error.message}
            </p>
          )}
          {membersQ.error && (
            <p role="alert" className="text-red-600 text-sm">
              {membersQ.error.message}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-2">Conta</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {(membersQ.data ?? []).map((m) => {
                  const role = { admin: 'tenant_admin', member: 'campaign_user' }[m.role] ?? m.role;
                  return (
                    <tr key={m.id} className="border-t border-slate-100">
                      <td className="py-3">{m.email}</td>
                      <td>{PROFILE_LABELS[role] ?? role}</td>
                      <td>{m.status === 'active' ? 'Ativo' : 'Inativo'}</td>
                      <td className="flex gap-2 py-2">
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => setMember({ email: m.email, role, status: 'active' })}
                        >
                          Editar
                        </button>
                        <button
                          className="btn-secondary"
                          type="button"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({
                              email: m.email,
                              role,
                              status: m.status === 'active' ? 'inactive' : 'active',
                            })
                          }
                        >
                          {m.status === 'active' ? 'Desativar' : 'Ativar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <p className="text-slate-500">Selecione um tenant para administrar seus usuários.</p>
      )}
    </div>
  );
}
